import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { 
  Activity, 
  Trash2, 
  RotateCcw,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  HardDrive,
  Cpu,
  MemoryStick,
  Terminal
} from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { SystemMetrics } from '../types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const System: React.FC = () => {
  const [selectedLogLevel, setSelectedLogLevel] = useState<'error' | 'warn' | 'info' | 'debug'>('error');
  const [metricsHistory, setMetricsHistory] = useState<any[]>([]);
  const queryClient = useQueryClient();

  const { data: metrics } = useQuery({
    queryKey: ['system-metrics'],
    queryFn: adminApi.getSystemMetrics,
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['system-logs', selectedLogLevel],
    queryFn: () => adminApi.getSystemLogs({ level: selectedLogLevel, limit: 100 }),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const clearCacheMutation = useMutation({
    mutationFn: adminApi.clearCache,
    onSuccess: () => {
      toast.success('Cache cleared successfully');
      queryClient.invalidateQueries();
    },
    onError: () => {
      toast.error('Failed to clear cache');
    },
  });

  const restartWorkersMutation = useMutation({
    mutationFn: adminApi.restartWorkers,
    onSuccess: () => {
      toast.success('Workers restarted successfully');
      queryClient.invalidateQueries({ queryKey: ['system-metrics'] });
    },
    onError: () => {
      toast.error('Failed to restart workers');
    },
  });

  // Real-time metrics via SSE
  useEffect(() => {
    const eventSource = adminApi.getSystemMetricsSSE();
    
    eventSource.onmessage = (event) => {
      const newMetrics: SystemMetrics = JSON.parse(event.data);
      
      // Update metrics history for charts
      setMetricsHistory(prev => {
        const newHistory = [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          cpu: newMetrics.cpuUsage,
          memory: (newMetrics.memoryUsage.used / newMetrics.memoryUsage.total) * 100,
          storage: (newMetrics.usedStorage / newMetrics.totalStorage) * 100,
        }];
        
        // Keep only last 20 data points
        return newHistory.slice(-20);
      });
      
      // Update query cache
      queryClient.setQueryData(['system-metrics'], newMetrics);
    };

    eventSource.onerror = () => {
      console.error('SSE connection failed for system metrics');
    };

    return () => {
      eventSource.close();
    };
  }, [queryClient]);

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getHealthStatus = (metrics?: SystemMetrics) => {
    if (!metrics) return { status: 'unknown', color: 'gray' };
    
    const cpuHigh = metrics.cpuUsage > 80;
    const memoryHigh = (metrics.memoryUsage.used / metrics.memoryUsage.total) > 0.9;
    const failedUploads = metrics.failedUploads > 10;
    
    if (cpuHigh || memoryHigh || failedUploads) {
      return { status: 'warning', color: 'yellow' };
    }
    
    return { status: 'healthy', color: 'green' };
  };

  const healthStatus = getHealthStatus(metrics);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            System Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor system health, manage resources, and view system logs
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => clearCacheMutation.mutate()}
            disabled={clearCacheMutation.isPending}
            className="btn-outline"
          >
            <Trash2 className={clsx('h-4 w-4 mr-2', clearCacheMutation.isPending && 'animate-spin')} />
            Clear Cache
          </button>
          <button
            onClick={() => restartWorkersMutation.mutate()}
            disabled={restartWorkersMutation.isPending}
            className="btn-primary"
          >
            <RotateCcw className={clsx('h-4 w-4 mr-2', restartWorkersMutation.isPending && 'animate-spin')} />
            Restart Workers
          </button>
        </div>
      </div>

      {/* System Health Overview */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">System Health</h3>
          <div className="flex items-center">
            <div className={clsx(
              'h-3 w-3 rounded-full mr-2',
              healthStatus.color === 'green' ? 'bg-green-500' :
              healthStatus.color === 'yellow' ? 'bg-yellow-500' : 'bg-gray-500'
            )} />
            <span className={clsx(
              'text-sm font-medium',
              healthStatus.color === 'green' ? 'text-green-600' :
              healthStatus.color === 'yellow' ? 'text-yellow-600' : 'text-gray-600'
            )}>
              {healthStatus.status === 'healthy' ? 'All Systems Operational' :
               healthStatus.status === 'warning' ? 'Performance Issues Detected' : 'Status Unknown'}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {formatUptime(metrics?.uptime || 0)}
            </div>
            <div className="text-sm text-gray-500">Uptime</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {metrics?.activeUploads || 0}
            </div>
            <div className="text-sm text-gray-500">Active Jobs</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {metrics?.totalUploads || 0}
            </div>
            <div className="text-sm text-gray-500">Total Uploads</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {metrics?.failedUploads || 0}
            </div>
            <div className="text-sm text-gray-500">Failed Uploads</div>
          </div>
        </div>
      </div>

      {/* Resource Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Resource Metrics */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Resource Usage</h3>
          <div className="space-y-4">
            <ResourceBar
              icon={Cpu}
              label="CPU Usage"
              value={metrics?.cpuUsage || 0}
              max={100}
              unit="%"
              color={metrics?.cpuUsage && metrics.cpuUsage > 80 ? 'red' : 'blue'}
            />
            
            <ResourceBar
              icon={MemoryStick}
              label="Memory Usage"
              value={metrics?.memoryUsage.used || 0}
              max={metrics?.memoryUsage.total || 100}
              unit=""
              formatValue={formatBytes}
              color={metrics?.memoryUsage && (metrics.memoryUsage.used / metrics.memoryUsage.total) > 0.9 ? 'red' : 'green'}
            />
            
            <ResourceBar
              icon={HardDrive}
              label="Storage Usage"
              value={metrics?.usedStorage || 0}
              max={metrics?.totalStorage || 100}
              unit=""
              formatValue={formatBytes}
              color={metrics?.usedStorage && metrics.totalStorage && (metrics.usedStorage / metrics.totalStorage) > 0.8 ? 'red' : 'purple'}
            />
          </div>
        </div>

        {/* Real-time Charts */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Trends</h3>
          <div className="h-64">
            {metricsHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="cpu" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="CPU %" 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="memory" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="Memory %" 
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Collecting performance data...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* System Logs */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">System Logs</h3>
            <div className="flex items-center space-x-3">
              <select
                value={selectedLogLevel}
                onChange={(e) => setSelectedLogLevel(e.target.value as any)}
                className="input text-sm"
              >
                <option value="error">Error</option>
                <option value="warn">Warning</option>
                <option value="info">Info</option>
                <option value="debug">Debug</option>
              </select>
              <button
                onClick={() => queryClient.invalidateQueries({ queryKey: ['system-logs'] })}
                className="btn-outline text-sm"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </button>
            </div>
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {logsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-twitch-600" />
            </div>
          ) : logs?.logs && logs.logs.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {logs.logs.map((log, index) => (
                <LogEntry key={index} log={log} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Terminal className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No {selectedLogLevel} logs found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ResourceBarProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  max: number;
  unit: string;
  formatValue?: (value: number) => string;
  color: 'blue' | 'green' | 'purple' | 'red';
}

const ResourceBar: React.FC<ResourceBarProps> = ({ 
  icon: Icon, 
  label, 
  value, 
  max, 
  unit, 
  formatValue, 
  color 
}) => {
  const percentage = (value / max) * 100;
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    red: 'bg-red-500',
  };

  return (
    <div>
      <div className="flex items-center justify-between text-sm font-medium text-gray-900 mb-1">
        <div className="flex items-center">
          <Icon className="h-4 w-4 mr-2 text-gray-500" />
          <span>{label}</span>
        </div>
        <span>
          {formatValue ? formatValue(value) : `${value}${unit}`}
          {max > 0 && !formatValue && ` / ${max}${unit}`}
          {formatValue && max > 0 && ` / ${formatValue(max)}`}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${colorClasses[color]} h-2 rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>0{unit}</span>
        <span>{percentage.toFixed(1)}%</span>
      </div>
    </div>
  );
};

interface LogEntryProps {
  log: {
    timestamp: string;
    level: string;
    message: string;
    meta?: any;
  };
}

const LogEntry: React.FC<LogEntryProps> = ({ log }) => {
  const getLogIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'warn':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'info':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error': return 'bg-red-100 text-red-800';
      case 'warn': return 'bg-yellow-100 text-yellow-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-4 hover:bg-gray-50">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          {getLogIcon(log.level)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLevelColor(log.level)}`}>
              {log.level.toUpperCase()}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(log.timestamp).toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-gray-900 font-mono">{log.message}</p>
          {log.meta && (
            <details className="mt-2">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                View metadata
              </summary>
              <pre className="mt-1 text-xs text-gray-600 bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(log.meta, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default System;